import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { rpc } from "../../lib/rpc";
import { uuid4 } from "../../hooks/useFlowOperationId";
import { AdminPageHeader, DataTable } from "../../components/admin/DataTable";
import { Button } from "../../components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function AdminInventur() {
    const [list, setList] = useState([]);
    const [locations, setLocations] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [selected, setSelected] = useState(null);
    // #136 Befund 2: Positionen mit Artikel + Fach-Code statt UUID anzeigen.
    const [positions, setPositions] = useState({});
    const [articles, setArticles] = useState({});
    const [compartments, setCompartments] = useState({});

    const load = () =>
        Promise.all([
            rpc.get("/inventories"),
            rpc.get("/master/locations"),
            rpc.get("/master/stock-positions"),
            rpc.get("/master/articles"),
            rpc.get("/master/compartments"),
        ]).then(([i, l, p, a, c]) => {
            if (i.ok) setList(i.data);
            if (l.ok) setLocations(l.data);
            if (p.ok) setPositions(Object.fromEntries(p.data.map((x) => [x.id, x])));
            if (a.ok) setArticles(Object.fromEntries(a.data.map((x) => [x.id, x])));
            if (c.ok) setCompartments(Object.fromEntries(c.data.map((x) => [x.id, x])));
        });
    useEffect(() => { load(); }, []);

    const positionLabel = (positionId) => {
        const pos = positions[positionId];
        if (!pos) return positionId.slice(0, 8);
        const article = articles[pos.article_id]?.name || "Artikel";
        const code = compartments[pos.compartment_id]?.code || "Fach";
        return `${article} · ${code}`;
    };

    const apply = async (invId) => {
        const res = await rpc.post(`/inventories/${invId}/apply`, { client_operation_id: uuid4() });
        if (!res.ok) return toast.error(res.error);
        toast.success(`Inventur geschlossen. ${res.data.applied_deltas ?? 0} Korrektur(en) gebucht.`);
        setSelected(null);
        load();
    };

    const columns = [
        { key: "name", label: "Name", render: (r) => r.name || r.id.slice(0, 8) },
        { key: "status", label: "Status", render: (r) => (r.status === "open" ? "offen" : "geschlossen") },
        {
            key: "created_at",
            label: "Erstellt",
            render: (r) => new Date(r.created_at).toLocaleDateString("de-DE"),
        },
    ];

    return (
        <>
            <AdminPageHeader title="Inventur" subtitle={`${list.length} Inventur(en)`}>
                <Button onClick={() => setShowCreate(true)} data-testid="inv-new">
                    <Plus className="mr-1 h-4 w-4" /> Neue Inventur
                </Button>
            </AdminPageHeader>
            <DataTable
                testId="admin-inv-table"
                columns={columns}
                rows={list}
                rowKey={(r) => r.id}
                onRowClick={(r) => setSelected(r)}
            />
            <InventurDetail
                inv={selected}
                onClose={() => setSelected(null)}
                onApply={apply}
                positionLabel={positionLabel}
            />
            <CreateInventoryDialog
                open={showCreate}
                onClose={() => setShowCreate(false)}
                locations={locations}
                onCreated={() => { setShowCreate(false); load(); }}
            />
        </>
    );
}

function CreateInventoryDialog({ open, onClose, locations, onCreated }) {
    const [name, setName] = useState("");
    const [scope, setScope] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (event) => {
        event.preventDefault();
        setBusy(true);
        const res = await rpc.post("/inventories", {
            name: name || undefined,
            scope_location_id: scope || undefined,
        });
        setBusy(false);
        if (!res.ok) return toast.error(res.error);
        toast.success("Inventur angelegt (mit Snapshot).");
        setName("");
        setScope("");
        onCreated();
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="inv-create-dialog">
                <DialogHeader><DialogTitle>Neue Inventur</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="flex flex-col gap-3">
                    <Label htmlFor="inv-new-name">Name (optional)</Label>
                    <Input id="inv-new-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} data-testid="inv-new-name" />
                    <Label>Scope (Standort)</Label>
                    <Select value={scope} onValueChange={setScope}>
                        <SelectTrigger><SelectValue placeholder="alle" /></SelectTrigger>
                        <SelectContent>
                            {locations.map((l) => (
                                <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button type="submit" disabled={busy} data-testid="inv-new-submit">
                        {busy ? "..." : "Anlegen + Snapshot"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function InventurDetail({ inv, onClose, onApply, positionLabel }) {
    const [items, setItems] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    useEffect(() => {
        if (!inv) return;
        setConfirmOpen(false);
        rpc.get(`/inventories/${inv.id}/items`).then((r) => r.ok && setItems(r.data));
    }, [inv]);
    if (!inv) return null;
    const counted = items.filter((i) => i.counted_quantity !== null && i.counted_quantity !== undefined).length;
    const diffItems = items.filter(
        (i) => i.counted_quantity != null && i.counted_quantity !== i.expected_quantity
    );
    const diff = diffItems.length;
    return (
        <Dialog open={!!inv} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-3xl" data-testid="inv-detail-dialog">
                <DialogHeader><DialogTitle>{inv.name || inv.id.slice(0, 8)}</DialogTitle></DialogHeader>
                <div className="mb-3 text-xs text-muted-foreground">
                    Status: <span className="text-foreground">{inv.status}</span> · Fortschritt: {counted}/{items.length} gezählt · {diff} Differenzen
                </div>
                <div className="max-h-80 overflow-auto rounded border border-border/60">
                    <table className="w-full text-xs">
                        <thead className="bg-card/60 uppercase tracking-wider">
                            <tr>
                                <th className="px-2 py-1 text-left">Position</th>
                                <th className="px-2 py-1 text-left">Erwartet</th>
                                <th className="px-2 py-1 text-left">Gezählt</th>
                                <th className="px-2 py-1 text-left">Diff</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => (
                                <tr key={it.id} className="border-t border-border/60">
                                    <td className="px-2 py-1 text-xs">{positionLabel(it.stock_position_id)}</td>
                                    <td className="px-2 py-1">{it.expected_quantity}</td>
                                    <td className="px-2 py-1">{it.counted_quantity ?? "—"}</td>
                                    <td className="px-2 py-1 text-warning">
                                        {it.counted_quantity != null ? it.counted_quantity - (it.expected_quantity ?? 0) : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {inv.status === "open" ? (
                    <Button className="mt-3" onClick={() => setConfirmOpen(true)} data-testid="inv-apply-btn">
                        Abschließen & Korrekturen buchen
                    </Button>
                ) : null}
                {/* #136 Befund 2: entscheidungsfähige Bestätigung mit
                    Delta-Zusammenfassung statt window.confirm. */}
                <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                    <AlertDialogContent data-testid="inv-apply-confirm">
                        <AlertDialogHeader>
                            <AlertDialogTitle>Inventur abschließen?</AlertDialogTitle>
                            <AlertDialogDescription>
                                {counted} von {items.length} Positionen gezählt.{" "}
                                {diff === 0
                                    ? "Keine Differenzen — es werden keine Buchungen erzeugt, die Inventur wird nur geschlossen."
                                    : `${diff} Differenz(en) werden als Korrekturen gebucht.`}{" "}
                                Ungezählte Positionen bleiben unverändert. Der Abschluss ist
                                nicht umkehrbar.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        {diff > 0 ? (
                            <div
                                className="max-h-48 overflow-auto rounded border border-border/60 text-xs"
                                data-testid="inv-apply-deltas"
                            >
                                <table className="w-full">
                                    <tbody>
                                        {diffItems.slice(0, 8).map((it) => (
                                            <tr key={it.id} className="border-t border-border/60 first:border-t-0">
                                                <td className="px-2 py-1">{positionLabel(it.stock_position_id)}</td>
                                                <td className="px-2 py-1 whitespace-nowrap text-muted-foreground">
                                                    {it.expected_quantity} → {it.counted_quantity}
                                                </td>
                                                <td className="px-2 py-1 whitespace-nowrap text-warning">
                                                    {it.counted_quantity - (it.expected_quantity ?? 0) > 0 ? "+" : ""}
                                                    {it.counted_quantity - (it.expected_quantity ?? 0)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {diffItems.length > 8 ? (
                                    <div className="px-2 py-1 text-muted-foreground">
                                        + {diffItems.length - 8} weitere Differenzen
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="inv-apply-cancel">Abbrechen</AlertDialogCancel>
                            <AlertDialogAction
                                data-testid="inv-apply-confirm-submit"
                                onClick={() => {
                                    setConfirmOpen(false);
                                    onApply(inv.id);
                                }}
                            >
                                Abschließen & buchen
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </DialogContent>
        </Dialog>
    );
}
